import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from './ThemeContext';
import {
  AFFILIATE_RFP_SECTIONS,
  buildEmptyRfpState,
} from '../data/rfpFieldConfig';
import { COMPLETE_TAXONOMY_DATA, getSubcategoriesForCategory, getAdProductForSelection } from '../data/TaxonomyData';
import { StructuredField } from './RfpForm';
import { TERMS_AND_CONDITIONS } from '../data/TermsAndConditions';
import InventoryPicker from './InventoryPicker';

function validateDmaOrCbsa(rfp) {
  const dma = rfp.audience?.dmaMarkets;
  const cbsa = rfp.audience?.cbsaMarkets;
  const d = Array.isArray(dma) ? dma.length : 0;
  const c = Array.isArray(cbsa) ? cbsa.length : 0;
  if (d + c < 1) return 'Select at least one DMA or one CBSA in Audience targeting.';
  return '';
}

const AffiliateCampaignForm = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const brandName = user?.name || user?.mail || '';

  const [campaignDisplayName, setCampaignDisplayName] = useState('');
  const [keyMessage, setKeyMessage] = useState('');
  const [selectedPmps, setSelectedPmps] = useState([]);
  const [availablePmps, setAvailablePmps] = useState([]);
  const [selectedDisplayUnitIds, setSelectedDisplayUnitIds] = useState([]);
  const [rfp, setRfp] = useState(() => buildEmptyRfpState());
  const [openSection, setOpenSection] = useState(AFFILIATE_RFP_SECTIONS[0]?.id || '');
  const [contentCategory, setContentCategory] = useState('');
  const [contentSubcategory, setContentSubcategory] = useState('');
  const [adProductCategory, setAdProductCategory] = useState('');
  const [files, setFiles] = useState([]);
  const [needContentCreated, setNeedContentCreated] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [submittedId, setSubmittedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/affiliate/pmp-options', { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setAvailablePmps(data.pmps || []);
        }
      } catch (e) {
        console.warn('PMP load failed', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (contentCategory) {
      const subs = getSubcategoriesForCategory(contentCategory);
      if (contentSubcategory && !subs.find((s) => s.value === contentSubcategory)) {
        setContentSubcategory('');
      }
    }
  }, [contentCategory, contentSubcategory]);

  useEffect(() => {
    if (contentCategory) {
      const ad = getAdProductForSelection(contentCategory, contentSubcategory);
      setAdProductCategory(ad || '');
    }
  }, [contentCategory, contentSubcategory]);

  const togglePmp = (value) => {
    setSelectedPmps((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const validate = () => {
    if (!(campaignDisplayName || '').trim()) return 'Campaign name is required.';
    if (!selectedDisplayUnitIds.length) return 'Select at least one live display unit.';
    if (!contentCategory) return 'Content category is required.';
    if (!contentSubcategory) return 'Content subcategory is required.';
    const geoErr = validateDmaOrCbsa(rfp);
    if (geoErr) return geoErr;
    const start = rfp.flighting?.flightScheduleStart;
    const end = rfp.flighting?.flightScheduleEnd;
    if (!start || !end) return 'Flight start and end dates are required.';
    if (!needContentCreated && !files.length) return 'Upload a creative asset or request content creation.';
    if (!termsAccepted) return 'Accept the terms and conditions.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setMsg(err);
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('advertiserName', brandName);
      fd.append('contactEmail', user?.mail || user?.email || '');
      fd.append('campaignName', campaignDisplayName.trim());
      fd.append('adType', 'affiliate');
      fd.append('startDate', rfp.flighting.flightScheduleStart);
      fd.append('endDate', rfp.flighting.flightScheduleEnd);
      fd.append('targetAudience', contentCategory);
      fd.append('specialInstructions', keyMessage || '');
      fd.append('recordKind', 'io');
      fd.append(
        'formDataJson',
        JSON.stringify({
          orderSource: 'affiliate_io',
          campaignDisplayName: campaignDisplayName.trim(),
          keyMessage,
          contentCategory,
          contentSubcategory,
          adProductCategory,
          selectedPmpTarget: 'routeOperations',
          selectedPmps,
          selectedDisplayUnitIds,
          needContentCreated,
          rfp,
        })
      );
      fd.append('rfpJson', JSON.stringify(rfp));
      if (!needContentCreated && files[0]) {
        fd.append('files', files[0]);
      }
      const r = await fetch('/api/orders', { method: 'POST', body: fd, credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.details || 'Submit failed');
      setSubmittedId(j.id || j.order?.id);
      setMsg(`Campaign submitted. Reference #${j.id || j.order?.id}. Our team will review your assets.`);
    } catch (ex) {
      setMsg(ex.message || String(ex));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm';

  const subcategories = contentCategory ? getSubcategoriesForCategory(contentCategory) : [];

  if (user?.userType !== 'affiliate') {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <p className="text-gray-700 dark:text-gray-300">This form is for affiliate accounts only.</p>
        <Link className="text-blue-600 mt-4 inline-block" to="/">
          Home
        </Link>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-8 px-4 ${isDark ? 'bg-gray-900' : 'bg-slate-50'}`}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Affiliate campaign request</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Advertiser: <strong>{brandName}</strong> — no media cost; campaigns run on your assigned PMPs after
            Context and publisher approval.
          </p>
        </div>

        {submittedId ? (
          <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/20 p-6">
            <p className="text-green-800 dark:text-green-200">{msg}</p>
            <Link to={`/status/${submittedId}`} className="text-blue-600 mt-3 inline-block">
              View status
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4 shadow-sm">
              <h2 className="font-semibold text-gray-900 dark:text-white">Campaign</h2>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Campaign name *
                </label>
                <input
                  className={inputCls}
                  value={campaignDisplayName}
                  onChange={(e) => setCampaignDisplayName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Key message / offer
                </label>
                <textarea
                  className={`${inputCls} min-h-[72px]`}
                  value={keyMessage}
                  onChange={(e) => setKeyMessage(e.target.value)}
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
              <InventoryPicker
                selectedIds={selectedDisplayUnitIds}
                onChange={setSelectedDisplayUnitIds}
              />
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4 shadow-sm">
              <h2 className="font-semibold text-gray-900 dark:text-white">Taxonomy *</h2>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Content category
                </label>
                <select
                  className={inputCls}
                  value={contentCategory}
                  onChange={(e) => setContentCategory(e.target.value)}
                >
                  <option value="">— Select —</option>
                  {COMPLETE_TAXONOMY_DATA.map((c) => (
                    <option key={c.category} value={c.category}>
                      {c.category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Content subcategory
                </label>
                <select
                  className={inputCls}
                  value={contentSubcategory}
                  onChange={(e) => setContentSubcategory(e.target.value)}
                  disabled={!contentCategory}
                >
                  <option value="">— Select —</option>
                  {subcategories.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              {adProductCategory ? (
                <p className="text-xs text-gray-500">Ad product: {adProductCategory}</p>
              ) : null}
            </div>

            {AFFILIATE_RFP_SECTIONS.map((sec) => (
              <div
                key={sec.id}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm"
              >
                <button
                  type="button"
                  className="w-full flex justify-between items-center px-4 py-3 text-left font-medium text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900/50"
                  onClick={() => setOpenSection(openSection === sec.id ? '' : sec.id)}
                >
                  {sec.title}
                  <span>{openSection === sec.id ? '−' : '+'}</span>
                </button>
                {openSection === sec.id && (
                  <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                    {sec.fields.map((f) => (
                      <div key={f.key}>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          {f.label}
                          {f.required ? ' *' : ''}
                        </label>
                        <StructuredField
                          f={f}
                          secId={sec.id}
                          rfp={rfp}
                          setRfp={setRfp}
                          inputCls={inputCls}
                          rfpAll={rfp}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 space-y-4 shadow-sm">
              <h2 className="font-semibold text-gray-900 dark:text-white">Creative assets *</h2>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={needContentCreated}
                  onChange={(e) => setNeedContentCreated(e.target.checked)}
                />
                I need Context to create content for this campaign
              </label>
              {!needContentCreated && (
                <input
                  type="file"
                  accept="image/*,video/*,.mp4,.mov,.jpg,.jpeg,.png"
                  onChange={(e) => setFiles(e.target.files ? [e.target.files[0]] : [])}
                />
              )}
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>
                  I accept the{' '}
                  <a href="#terms" className="text-blue-600 underline">
                    terms and conditions
                  </a>
                </span>
              </label>
              <details id="terms" className="mt-4 text-xs text-gray-600 dark:text-gray-400 max-h-48 overflow-y-auto">
                <summary className="cursor-pointer font-medium">View terms</summary>
                <pre className="whitespace-pre-wrap mt-2">{TERMS_AND_CONDITIONS}</pre>
              </details>
            </div>

            {msg && !submittedId ? (
              <p className="text-sm text-red-600 dark:text-red-400">{msg}</p>
            ) : null}

            <button
              type="submit"
              disabled={busy}
              className="w-full sm:w-auto px-8 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium"
            >
              {busy ? 'Submitting…' : 'Submit campaign'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AffiliateCampaignForm;
